import React, {
  useCallback,
  useMemo,
  useRef,
  useState,
} from 'react';
import PropTypes from 'prop-types';
import isString from 'lodash/fp/isString';

import { makeStyles } from '@material-ui/core/styles';
import FormControl from '@material-ui/core/FormControl';
import Button from '@material-ui/core/Button';

const useStyles = makeStyles((theme) => ({
  container: {
    display: 'flex',
    flexWrap: 'wrap',
  },
  formControl: {
    width: '100%',
  },
  input: {
    display: 'none',
  },
  label: {
    display: 'flex',
    alignItems: 'center',
  },
  button: {
    backgroundColor: '#3f50b5 !important',
    color: 'white',
  },
  filename: {
    paddingLeft: '5px',
    cursor: 'pointer',
    width: '60%',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    lineHeight: '25px',
  },
  validation: {
    color: '#f44336',
    marginTop: '5px',
  },
}));

const acceptVariants = {
  word:
    '.pdf,.doc,.docx,.xml,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  images: 'image/*',
  excel:
    '.xlsx,.xls,.csv,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  csv: '.csv',
};

const makeAcceptString = (accept) => {
  if (!accept || !accept.length) {
    return '';
  }

  if (isString(accept)) {
    return acceptVariants[accept] ? acceptVariants[accept] : accept;
  }

  return '';
};

const FileInput = ({
 accept,
 multiple = false,
 id = 'file-input',
 inputProps,
 labelProps,
 buttonProps,
 onChange,
 validation,
 label = 'Add File',
 filepath,
}) => {
  const classes = useStyles();

  const acceptString = useMemo(() => makeAcceptString(accept), [accept]);

  const [path, setPath] = useState(filepath || 'Select a file from computer');

  const inputRef = useRef(null);
  const handleChange = useCallback(
    (e) => {
      const { value } = inputRef.current;

      if (value) setPath(value);

      if (typeof onChange === 'function') {
        onChange(e);
      }
    },
    [inputRef],
  );

  return (
    <div className={classes.container}>
      <FormControl className={classes.formControl}>
        <input
          id={id}
          accept={acceptString}
          className={classes.input}
          multiple={multiple}
          type="file"
          onChange={handleChange}
          ref={inputRef}
          {...inputProps}
        />
        <label className={classes.label} htmlFor={id} {...labelProps}>
          <Button
            variant="contained"
            component="span"
            className={classes.button}
            {...buttonProps}
          >
            {label}
          </Button>
          <span className={classes.filename} title={path}>
            {path}
          </span>
        </label>
        <div className={classes.validation}>
          {validation[inputProps.name]}
        </div>
      </FormControl>
    </div>
  );
};

FileInput.propTypes = {
  accept: PropTypes.string,
  filepath: PropTypes.string,
  multiple: PropTypes.bool,
  id: PropTypes.string,
  inputProps: PropTypes.objectOf(PropTypes.any),
  labelProps: PropTypes.objectOf(PropTypes.any),
  buttonProps: PropTypes.objectOf(PropTypes.any),
  onChange: PropTypes.func,
  label: PropTypes.string,
  validation: PropTypes.shape({
    file: PropTypes.string,
  }),
};

export default FileInput;
